import type { CompositeDef, CompositeState, CompositeTemplateId, SceneCompositeInstance } from './types';
import type { CompositeInstance } from './CompositeInstance';
import type { Scene } from '../scene/Scene';

/**
 * A state schema field describes one named property in a template's state map.
 * The scene editor's generic inspector renders the appropriate control per kind.
 */
export type StateFieldSchema =
  | { readonly kind: 'number'; readonly min: number; readonly max: number; readonly default: number }
  | { readonly kind: 'integer'; readonly min: number; readonly max: number; readonly default: number }
  | { readonly kind: 'boolean'; readonly default: boolean }
  | { readonly kind: 'enum'; readonly values: readonly string[]; readonly default: string };

export type StateSchema = Readonly<Record<string, StateFieldSchema>>;

/**
 * Template definition — declares the typed state schema and provides the
 * runtime builder. v1 ships exactly one template (lightEmitter). Adding a new
 * template = a new file + a `registerTemplate` call.
 */
export interface Template {
  readonly id: CompositeTemplateId;
  readonly stateSchema: StateSchema;
  /** Defaults for any state field the placement doesn't override. */
  readonly defaultState: CompositeState;
  /**
   * Instantiate runtime objects for one placement. The implementation:
   *   - resolves slot data (mesh, light, behaviours),
   *   - registers them with Scene (objectManager / lightManager),
   *   - tags children with `compositeInstanceId` for picking escalation,
   *   - returns the CompositeInstance handle.
   */
  buildRuntime(
    def: CompositeDef,
    placement: SceneCompositeInstance,
    mergedState: CompositeState,
    scene: Scene,
  ): CompositeInstance;
}

const registry = new Map<CompositeTemplateId, Template>();

export function registerTemplate(t: Template): void {
  registry.set(t.id, t);
}

export function getTemplate(id: CompositeTemplateId): Template | undefined {
  return registry.get(id);
}

export function requireTemplate(id: CompositeTemplateId): Template {
  const t = registry.get(id);
  if (!t) throw new Error(`Unknown composite template: ${id}`);
  return t;
}

/** Merge defaults under placement overrides. */
export function resolveState(
  template: Template,
  placement: SceneCompositeInstance,
  override?: Partial<CompositeState>,
): CompositeState {
  const merged: CompositeState = { ...template.defaultState };
  if (placement.state) {
    for (const [k, v] of Object.entries(placement.state)) {
      if (v !== undefined) merged[k] = v;
    }
  }
  if (override) {
    for (const [k, v] of Object.entries(override)) {
      if (v !== undefined) merged[k] = v;
    }
  }
  return merged;
}
